'use client';

import type { ChangeEvent } from 'react';

import { Card, Slider, TextInput, Typography } from '@polyseme/design-system';
import { cn } from '@polyseme/utils/cn/cn-utils';
import { useDebounceCallback } from '@polyseme/utils/hooks/use-debounce-callback/use-debounce-callback';
import { forwardRef, useEffect, useId, useState } from 'react';

const formatAmount = (amount: number): string =>
  amount.toLocaleString('fr-FR').replace(/,/g, ' ');

const parseAmount = (amount: string): number =>
  Number.parseInt(amount.replace(/\s/g, '')) || 0;

type RangeSliderInputProps = {
  className?: string;
  helper?: string;
  label: string;
  max: number;
  min: number;
  onChange: (value: number) => void;
  step: number;
  value: number;
};

export const RangeSliderInput = forwardRef<
  HTMLDivElement,
  RangeSliderInputProps
>(({ className, helper, label, max, min, onChange, step, value }, ref) => {
  const id = useId();
  const [inputValue, setInputValue] = useState('');
  const [sliderValue, setSliderValue] = useState(value);

  const debouncedOnChange = useDebounceCallback(onChange, 300);

  useEffect(() => {
    setInputValue(formatAmount(value));
    setSliderValue(value);
  }, [value]);

  const handleInputChange = ({ target }: ChangeEvent<HTMLInputElement>) =>
    setInputValue(target.value);

  const handleInputBlur = () => {
    const numericValue = parseAmount(inputValue);

    const correctedValue = Math.min(max, Math.max(numericValue, min));

    setInputValue(formatAmount(correctedValue));
    onChange(correctedValue);
  };

  const handleSliderChange = ([amount]: number[]) => {
    if (amount === undefined) return;
    setSliderValue(amount);
    setInputValue(formatAmount(amount));
    debouncedOnChange(amount);
  };

  return (
    <Card
      ref={ref}
      className={cn(
        'flex w-full flex-col gap-4 border-gray-200 bg-gray-50 p-6',
        className
      )}
    >
      <div className="flex flex-col gap-2">
        <div>
          <label className="text-base font-medium text-foreground" htmlFor={id}>
            <Typography variant="body">{label}</Typography>
          </label>
        </div>
        <div className="flex items-center gap-2">
          <TextInput
            className="w-full"
            id={id}
            inputMode="numeric"
            rightIcon="euro"
            type="text"
            value={inputValue}
            onBlur={handleInputBlur}
            onChange={handleInputChange}
          />
        </div>
      </div>

      <div className="flex flex-col gap-2 px-1">
        <Slider
          aria-label={label}
          className="w-full"
          max={max}
          min={min}
          step={step}
          value={[sliderValue]}
          onValueChange={handleSliderChange}
        />
        {!!helper && (
          <Typography className="text-xs text-gray-500">{helper}</Typography>
        )}
      </div>
    </Card>
  );
});

RangeSliderInput.displayName = 'RangeSliderInput';
